import styled from 'styled-components';
import { Spinner } from 'react-rainbow-components';
import EmptyMessage from 'components/EmptyMessage';
import LoadingShape from 'components/LoadingShape';
import Picklist from 'components/Picklist';
import {
    DIVIDER,
    GREY_100,
    GREY_300,
    GREY_500,
    GREY_800,
    PURPLE_100,
    PURPLE_500,
    PURPLE_600,
    WHITE,
} from '../../../constants';

export const Container = styled.div`
    padding: 0.75rem 0;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    gap: 1rem;
    height: 100%;
    min-height: 0;
`;

export const Content = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 0.5rem 1.5rem 1.5rem;
    box-sizing: border-box;
`;

export const PatientHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0 2rem;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid ${DIVIDER};
`;

export const PatientInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 0.25rem;
`;

export const PatientName = styled.h4`
    font-size: 1.25rem;
    font-weight: 400;
    line-height: 1.75rem;
    color: ${GREY_800};
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const PatientMeta = styled.p`
    font-size: 0.875rem;
    font-weight: 300;
    line-height: 1.25rem;
    color: ${GREY_500};
`;

export const PatientBadges = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;

export const Section = styled.section`
    display: flex;
    flex-direction: column;
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.5rem;
`;

export const SectionTitle = styled.div`
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: ${GREY_300};
    text-transform: uppercase;
`;

export const DetailsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 1.5rem;

    @media (max-width: 1023px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

export const AlertItem = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 0;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5rem;
    color: ${GREY_800};
    border-bottom: 1px solid ${DIVIDER};
`;

export const AlertDate = styled.span`
    font-size: 0.8125rem;
    font-weight: 300;
    color: ${GREY_500};
    flex-shrink: 0;
`;

export const AppointmentItem = styled.div`
    display: flex;
    padding: 0.75rem 0;
    align-items: center;
    gap: 1rem;
    border-bottom: 1px solid ${DIVIDER};
`;

export const SkeletonDate = styled(LoadingShape)`
    margin: 0;
    flex-shrink: 0;

    > div {
        border-radius: 0.75rem;
    }
`;

export const SkeletonLine = styled(LoadingShape)`
    > div {
        border-radius: 1rem;
    }
`;

export const DateAvatar = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 0.75rem;
    flex-shrink: 0;
    background: ${GREY_100};
    color: ${GREY_500};
`;

export const DateMonth = styled.span`
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 0.875rem;
    text-transform: uppercase;
`;

export const DateDay = styled.span`
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.375rem;
`;

export const AppointmentInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 0.125rem;
`;

export const AppointmentTitle = styled.span`
    font-size: 0.9375rem;
    font-weight: 400;
    line-height: 1.375rem;
    color: ${GREY_800};
`;

export const AppointmentDetails = styled.span`
    font-size: 0.8125rem;
    font-weight: 300;
    line-height: 1.25rem;
    color: ${GREY_500};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const AppointmentStatuses = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;

export const AppointmentsList = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
    border: 1px solid ${DIVIDER};
    border-radius: 0.75rem;

    > :last-child {
        border-bottom: none;
    }
`;

export const NextAppointmentItem = styled(AppointmentItem)`
    ${DateAvatar} {
        background: ${PURPLE_500};
        color: ${WHITE};
    }

    ${AppointmentTitle} {
        color: ${PURPLE_600};
    }

    ${AppointmentDetails} {
        color: ${PURPLE_500};
    }
`;

export const RangeFilter = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const RangeLabel = styled.span`
    font-size: 0.8125rem;
    font-weight: 400;
    color: ${GREY_500};
`;

export const RangePicklist = styled(Picklist)`
    width: 9.5rem;
`;

export const StyledEmptyMessage = styled(EmptyMessage)`
    padding: 0.75rem 0;
    border-bottom: 1px solid ${DIVIDER};

    p {
        font-size: 0.875rem;
        font-weight: 300;
        color: ${GREY_500};
    }
`;

export const EmptyMessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    margin: 6rem auto 1rem auto;
    padding: 0 1rem;
    gap: 0.5rem;
    width: 100%;
    max-width: 35rem;
`;

export const EmptyMessageIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4rem;
    height: 4rem;
    margin-bottom: 0.5rem;
    border-radius: 50%;
    flex-shrink: 0;
    background-color: ${PURPLE_100};
    color: ${PURPLE_500};

    > svg {
        width: 1.75rem;
        height: 1.75rem;
    }
`;

export const EmptyMessageTitle = styled.h1`
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 2rem;
    color: ${GREY_800};
`;

export const EmptyMessageDescription = styled.p`
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5rem;
    color: ${GREY_800};
    margin-bottom: 0.5rem;
`;

export const SpinnerContainer = styled.div`
    position: relative;
    height: 6rem;
`;

export const StyledSpinner = styled(Spinner)``;
